import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../../lib/api'
import type { StatementRow, WalletStanding } from '../../lib/contract'
import { money } from '../../components/Threshold'
import styles from './Wallet.module.css'

/**
 * THE STATEMENT — *"why is my balance this?"*, answerable at any time.
 *
 * The covenant's trust posture is mostly this page existing. Every row explains itself, in the
 * player's own money, with what it was for; nothing is aggregated into a figure they have to take
 * on faith. `docs/guides/the-wallet.md` §4.5, §5.
 *
 * ⚠ NEVER SURFACED DURING PLAY, and there is no route into it from inside a sitting. A balance a
 * player can reach mid-scene is a meter one tap away, which is the thing §3 forbids.
 *
 * ⚠ IT SHOWS OUR COMPUTE COST BESIDE WHAT WE CHARGED. Radical cost transparency (§6) as a column
 * rather than a claim on a marketing page — and it keeps being true when the models move under us,
 * because it is measured rather than written down.
 */
export default function Wallet() {
  const [standing, setStanding] = useState<WalletStanding | null>(null)
  const [rows, setRows] = useState<StatementRow[] | null>(null)

  useEffect(() => {
    let live = true
    void Promise.all([api.wallet(), api.statement()])
      .then(([w, s]) => {
        if (!live) return
        setStanding(w)
        setRows(s.rows)
      })
      .catch(() => live && setRows([]))
    return () => {
      live = false
    }
  }, [])

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <Link to="/" className={styles.back}>
          back
        </Link>
      </header>

      {/*
        The balance, stated once and plainly. `held` appears only when something is actually held,
        because explaining a hold to somebody who has none is the product describing its own
        machinery at a reader who did not ask.
      */}
      <p className={styles.balance}>
        {standing?.enabled === false ? '—' : money(standing?.balanceMinor ?? 0, standing?.currency ?? 'USD')}
      </p>
      {standing?.enabled === false && (
        <p className={styles.note}>No wallet is configured on this build.</p>
      )}
      {standing?.enabled && standing.heldMicros > 0 && (
        <p className={styles.note}>
          {money(Math.floor(standing.heldMicros / 10000), standing.currency)} is booked against a
          sitting that has not ended. It returns when it does.
        </p>
      )}

      {rows !== null && rows.length === 0 && standing?.enabled !== false && (
        <p className={styles.note}>Nothing yet.</p>
      )}

      <div className={styles.rows}>
        {(rows ?? []).map((r) => (
          <div key={r.id} className={styles.row}>
            <span className={styles.what}>{describe(r)}</span>
            <span className={r.amountMinor < 0 ? styles.out : styles.in}>
              {r.amountMinor < 0 ? '−' : '+'}
              {money(Math.abs(r.amountMinor), r.currency)}
            </span>
            <span className={styles.when}>{when(r.at)}</span>
            {/* What it cost us to run, beside what we charged for it. */}
            {r.costUsd != null && (
              <span className={styles.cost}>ran on {computeCost(r.costUsd)} of compute</span>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

function describe(r: StatementRow) {
  switch (r.kind) {
    case 'settlement':
      return r.reference?.split(':')[0] ?? 'a sitting'
    case 'topup':
      return 'added to your wallet'
    case 'grant':
      return r.note ?? 'given'
    case 'refund':
      return 'returned'
    default:
      return r.note ?? 'adjustment'
  }
}

/**
 * OUR compute cost, always in USD and always with fractions of a cent visible.
 *
 * ⚠ DELIBERATELY NOT THE PLAYER'S CURRENCY, and deliberately not rounded. This is the one number
 * on the page that is not their money — it is what the sitting cost us to run, quoted in the
 * currency we actually pay the model in, at the grain it actually happens. Converting it into
 * their money would invent an exchange rate to make a transparency claim, which would be the
 * claim eating itself.
 */
const computeCost = (usd: number) => (usd < 0.01 ? `${(usd * 100).toFixed(2)}¢` : `$${usd.toFixed(2)}`)

const when = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
